import {
  Button,
  IconButton,
  InputAdornment,
  Menu,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
} from '@material-ui/core';
import { AddCircle, MoreHoriz } from '@material-ui/icons';
import CreateIcon from '@material-ui/icons/Create';
import DeleteForeverIcon from '@material-ui/icons/DeleteForever';
import SearchIcon from '@material-ui/icons/Search';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import PublishIcon from '@mui/icons-material/Publish';
import VisibilityIcon from '@mui/icons-material/Visibility';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PreviewPopup from '../../../../components/ui-builder/preview-popup/PreveiwPopup';
import {
  UIBUILDER_PAGE_BUILDER,
  UIBUILDER_PAGE_CREATE,
  UIBUILDER_PAGE_EDIT,
} from '../../../../constants/UiBuilderConst';
import DeletePopupModel from '../../../../shared/delete-popup/Delete-Popup';
import DuplicatePopupModel from '../../../../shared/duplicate-popup/Duplicate-Popup';
import LinearProgressBar from '../../../../shared/progress-bar/Progress';
import TablePaginationActions from '../../../../shared/table-pagination/TablePaginationActions';
import WarningPopup from '../../../../shared/warning-popup/Warning-Popup';
import { formatDateString } from '../../../../utils/commonUtils';
import { notificationManager } from '../../../../utils/NotificationUtils';
import {
  deleteUIBuilderPageAPI,
  duplicateUIBuilderPageAPI,
  publishUiBuilderPage,
} from '../../../../utils/UiBuilderAPIUtils';
import {
  allUIBuilderPagesSelector,
  fetchAllUIBuilderPages,
  selectedHotelSelector,
} from '../../../property/propertySlice';
import { decryptLocalStorage } from '../../../../utils/authUtils';
import { stepDownLoaderCount, stepUpLoaderCount } from '../../../../utils/loaderSlice';

const UiBuilderPagesList = (props) => {
  const {
    onDrawerToggle,
    onViewToggle,
    onsetPage,
    pageNo,
    onsetRowsPerPage,
    rowsPerPage,
    showLoader = () => {},
  } = props;
  const initialSelectedValue = {
    id: '',
    name: '',
    title: '',
    description: '',
    status: false,
    anchorEl: null,
    type: '',
    duplicateStatus: false,
    viewPubishStatus: false,
  };
  const [selectedValue, setSelectedValue] = useState(initialSelectedValue);
  const originalPages = useSelector(allUIBuilderPagesSelector);
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const dispatch = useDispatch();
  const selectedHotel = useSelector(selectedHotelSelector);
  const [tempSearchText, setTempSearchText] = useState('');
  const [searchButton, setSearchButton] = useState(true);
  const prevHotelIdRef = useRef(selectedHotel);

  useEffect(() => {
    if (!originalPages && originalPages?.length === 0) {
      setPages([]);
      setTempSearchText('');
      setSearchButton(true);
    } else {
      setPages(originalPages);
      setTempSearchText('');
      setSearchButton(true);
    }
    setLoading(false);
  }, [originalPages]);

  useEffect(() => {
    if (loading || pages?.length === 0) return;
    const totalItems = pages?.length;
    const maxPageIndex = Math.max(Math.ceil(totalItems / rowsPerPage) - 1, 0);
    if (pageNo > maxPageIndex) {
      onsetPage(maxPageIndex);
    }
  }, [pages?.length, rowsPerPage, pageNo, onsetPage]);

  useEffect(() => {
    if (prevHotelIdRef?.current !== selectedHotel) {
      onsetPage(0);
    }
    prevHotelIdRef.current = selectedHotel;
  }, [selectedHotel]);

  const setSearchText = (searchText) => {
    setTempSearchText(searchText);
    const filteredRows = originalPages.filter((item) => {
      return item.name.toLowerCase().includes(searchText.toLowerCase());
    });
    setPages(filteredRows);
    onsetPage(0);
  };

  const handleChangePage = (event, newPage) => {
    onsetPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    onsetRowsPerPage(parseInt(event.target.value, 10));
    onsetPage(0);
  };

  const onOpenDrawer = (event, type) => {
    event.stopPropagation();
    onDrawerToggle(type, true, selectedValue.page);
    handleClose();
  };

  const handleClose = () => {
    setSelectedValue(initialSelectedValue);
  };

  const handleCellMenuClick = (event, page) => {
    event.stopPropagation();
    setSelectedValue({
      ...initialSelectedValue,
      id: page.id,
      name: page.name,
      anchorEl: event.currentTarget,
      page: page,
    });
  };

  const deletePage = async () => {
    try {
      const selectedLanguage = decryptLocalStorage('lang');
      const language = selectedLanguage !== 'en' ? selectedLanguage : '';
      if (selectedHotel && selectedValue && selectedValue.id) {
        await deleteUIBuilderPageAPI(selectedHotel, selectedValue.id, language);
        dispatch(fetchAllUIBuilderPages(selectedHotel));
        notificationManager('success', 'Page deleted successfully');
      }
    } catch (e) {
      if (e.graphQLErrors) {
        notificationManager('danger', e.graphQLErrors[0].message);
      } else {
        notificationManager('danger', 'Failed to delete Page');
      }
    }
    handleClose();
  };

  const onViewDetails = (page, e, classDisabled) => {
    if (classDisabled) return onViewToggle(UIBUILDER_PAGE_BUILDER, true, page);
    if (e.target.className === 'MuiTableCell-root MuiTableCell-body') {
      onViewToggle(UIBUILDER_PAGE_BUILDER, true, page);
    }
  };

  const onClickViewPublished = () => {
    setSelectedValue((prev) => ({
      ...prev,
      viewPubishStatus: true,
      anchorEl: null,
    }));
  };

  const onClickPublish = async (event) => {
    try {
      dispatch(stepUpLoaderCount());
      const selectedLanguage = decryptLocalStorage('lang');
      const language = selectedLanguage !== 'en' ? selectedLanguage : '';
      event.stopPropagation();
      await publishUiBuilderPage(selectedHotel, selectedValue.id, language);
      dispatch(fetchAllUIBuilderPages(selectedHotel));
      notificationManager('success', 'Page published successfully');
      handleClose();
    } catch (error) {
      if (error?.graphQLErrors?.[0]?.message.includes('exact copy')) {
        const copyOf = error.graphQLErrors[0]?.message.split(': ');
        setSelectedValue((prev) => ({
          ...prev,
          title: '',
          description: `The selected page cannot be published as it is a duplicate of ${copyOf[1]} with the same content & components.`,
          status: true,
          anchorEl: null,
          type: 'Publish',
          name: selectedValue.page.name,
        }));
        return false;
      }
      notificationManager('danger', error?.graphQLErrors[0]?.message ?? 'Failed to publish page');
      handleClose();
    } finally {
      dispatch(stepDownLoaderCount());
    }
  };

  const onClickDelete = (event) => {
    event.stopPropagation();
    setSelectedValue((prev) => ({
      ...prev,
      title: selectedValue.page.status === 'Published' ? '' : 'Are you sure you want to delete',
      description:
        selectedValue.page.status === 'Published'
          ? 'The selected page cannot be deleted as it is already published to the Guest Mobile App'
          : 'This action cannot be reversed. All your changes for this page will be lost.',
      status: true,
      anchorEl: null,
      type: selectedValue.page.status === 'Published' ? 'delete' : 'deleteModal',
    }));
  };

  const onClickDuplicate = (event) => {
    event.stopPropagation();
    setSelectedValue((prev) => ({
      ...prev,
      anchorEl: null,
      duplicateStatus: true,
      title: 'Are you sure you want to Duplicate',
      description: 'Please provide a name to be assigned for the duplicated page.',
    }));
  };

  const duplicatePage = async (event, name) => {
    event.stopPropagation();
    try {
      const selectedLanguage = decryptLocalStorage('lang');
      const language = selectedLanguage !== 'en' ? selectedLanguage : '';
      if (selectedHotel && selectedValue && selectedValue.id && name) {
        await duplicateUIBuilderPageAPI(selectedHotel, selectedValue.id, name, language);
        dispatch(fetchAllUIBuilderPages(selectedHotel));
        notificationManager('success', 'Page duplicated successfully');
      }
    } catch (e) {
      if (e.graphQLErrors) {
        notificationManager('danger', e.graphQLErrors[0].message);
      } else {
        notificationManager('danger', 'Failed to duplicate Page');
      }
    }
    handleClose();
  };

  const handleEditClick = () => {
    if (selectedValue && selectedValue.page) {
      onViewToggle(UIBUILDER_PAGE_BUILDER, true, selectedValue.page);
    }
  };

  return (
    <div className='mt-4 mb-4 group-list-container'>
      {selectedValue.id &&
        selectedValue.status === true &&
        selectedValue.type === 'deleteModal' && (
          <DeletePopupModel
            deletePopUp={selectedValue}
            onDelete={deletePage}
            onDeleteCancel={handleClose}
          />
        )}
      {selectedValue.status === true &&
        (selectedValue.type === 'Publish' || selectedValue.type === 'delete') && (
          <WarningPopup warningPopup={selectedValue} onWarningClose={handleClose} />
        )}
      {selectedValue.duplicateStatus === true && selectedValue.id && (
        <DuplicatePopupModel
          duplicatePopUp={selectedValue}
          onDuplicate={duplicatePage}
          onDuplicateCancel={handleClose}
        />
      )}
      {selectedValue.viewPubishStatus === true && selectedValue.id && (
        <PreviewPopup
          viewPubishStatus={selectedValue.viewPubishStatus}
          page={selectedValue.page}
          handleClose={handleClose}
          onEditClick={handleEditClick}
        />
      )}
      <div className='row mx-2 d-flex'>
        <div className='col-12 col-lg-4'>
          {pages && (
            <TextField
              className='w-100'
              onChange={(e) => {
                setSearchText(e.target.value);
                setSearchButton(e?.target?.value ? false : true);
              }}
              label='Search'
              value={tempSearchText}
              InputProps={
                searchButton && {
                  endAdornment: (
                    <InputAdornment position='end'>
                      <IconButton>
                        <SearchIcon />
                      </IconButton>
                    </InputAdornment>
                  ),
                }
              }
            />
          )}
        </div>
        <div className='col-12 col-lg-8 pt-3 pt-lg-0 d-flex justify-content-end'>
          <Button
            variant='contained'
            color='primary'
            size='large'
            startIcon={<AddCircle />}
            onClick={(e) => {
              onOpenDrawer(e, UIBUILDER_PAGE_CREATE);
            }}
          >
            Add Page
          </Button>
        </div>
      </div>
      <div className='row mx-2 d-flex mt-4'>
        {loading ? (
          <div className='pt-4 col-md-12'>
            <LinearProgressBar />
          </div>
        ) : (
          pages && (
            <TableContainer>
              <Table className='list-table' aria-label='simple table'>
                <TableHead>
                  <TableRow>
                    <TableCell className='w-40'>Page Name</TableCell>
                    <TableCell className='w-20'>Last Edited</TableCell>
                    <TableCell className='w-20'>Last Published</TableCell>
                    <TableCell className='w-30' align='center'>
                      Status
                    </TableCell>
                    <TableCell align='right'></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(rowsPerPage > 0
                    ? pages.slice(pageNo * rowsPerPage, pageNo * rowsPerPage + rowsPerPage)
                    : pages
                  ).map((page, i) => (
                    <TableRow
                      key={i}
                      className='border-white pointer'
                      onClick={(e) => {
                        onViewDetails(page, e);
                      }}
                    >
                      <TableCell>{page.name}</TableCell>
                      <TableCell>
                        {formatDateString(page?.updatedAt ?? page?.createdAt) ?? ''}
                      </TableCell>
                      <TableCell>
                        {page?.publishedAt !== 0 ? formatDateString(page.publishedAt) : '----'}
                      </TableCell>
                      <TableCell onClick={(e) => onViewDetails(page, e, true)}>
                        {' '}
                        <div className='d-flex justify-content-center'>
                          <div
                            className={`${
                              page.status.toLowerCase() === 'draft'
                                ? 'pill-inactive theme-dark-green '
                                : 'pill-active '
                            } pill d-flex justify-content-center align-items-center`}
                          >
                            <span className='ms-1'>{page.status}</span>
                          </div>
                        </div>
                      </TableCell>

                      <TableCell align='right'>
                        <Button
                          aria-controls='simple-menu'
                          aria-haspopup='true'
                          onClick={(e) => {
                            handleCellMenuClick(e, page);
                          }}
                        >
                          <MoreHoriz />
                        </Button>
                        <Menu
                          id={i}
                          anchorEl={selectedValue?.anchorEl}
                          disableRestoreFocus
                          MenuListProps={{ autoFocusItem: false }}
                          keepMounted
                          open={Boolean(selectedValue.anchorEl) && selectedValue.id === page.id}
                          onClose={handleClose}
                          className='shadow'
                        >
                          {selectedValue?.page?.status !== 'Published' &&
                            selectedValue?.page?.uiConfiguration !== '' && (
                              <MenuItem className='pe-4' onClick={(e) => onClickPublish(e)}>
                                <PublishIcon className='me-2 action-menu-icon' />
                                Publish
                              </MenuItem>
                            )}
                          <MenuItem
                            className='pe-4'
                            onClick={(e) => {
                              onOpenDrawer(e, UIBUILDER_PAGE_EDIT);
                            }}
                          >
                            <CreateIcon className='me-2 action-menu-icon' />
                            {selectedValue?.page?.status === 'Published'
                              ? 'Rename page'
                              : 'Edit Details'}
                          </MenuItem>
                          {page?.publishedAt !== 0 && (
                            <MenuItem
                              className='pe-4'
                              onClick={(e) => {
                                onClickViewPublished();
                              }}
                            >
                              <VisibilityIcon className='me-2 action-menu-icon' />
                              View Published
                            </MenuItem>
                          )}
                          <MenuItem className='pe-4' onClick={(e) => onClickDuplicate(e)}>
                            <ContentCopyIcon className='me-2 action-menu-icon' />
                            Duplicate
                          </MenuItem>
                          <MenuItem className='pe-4' onClick={(e) => onClickDelete(e)}>
                            <DeleteForeverIcon className='me-2 action-menu-icon' />
                            Delete
                          </MenuItem>
                        </Menu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
                <TableFooter>
                  <TableRow className='paginator'>
                    <TablePagination
                      rowsPerPageOptions={[5, 10, 25, { label: 'All', value: -1 }]}
                      count={pages.length}
                      rowsPerPage={rowsPerPage}
                      page={pageNo}
                      SelectProps={{
                        inputProps: { 'aria-label': 'rows per page' },
                        native: true,
                      }}
                      onPageChange={handleChangePage}
                      onRowsPerPageChange={handleChangeRowsPerPage}
                      ActionsComponent={TablePaginationActions}
                    />
                  </TableRow>
                </TableFooter>
              </Table>
            </TableContainer>
          )
        )}
      </div>
    </div>
  );
};

export default UiBuilderPagesList;
